import React, { useState, useEffect } from "react";
import BusinessDataService from "../services/BusinessService";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Stack,
  Button,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  alpha,
  useTheme,
  Card
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { TENOR_MARGIN_DEFAULTS } from "../../components/BusinessProfitCalculator";
import { fNumber } from "../../utils/formatNumber";

const Business = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Tenor restructuring state
  const [newTenor, setNewTenor] = useState("");
  const [newMarginRate, setNewMarginRate] = useState("");
  const [tenorReason, setTenorReason] = useState("");

  const getBusiness = (docId) => {
    setLoading(true);
    BusinessDataService.get(docId)
      .then((response) => {
        const data = response.data || {};
        setCurrentBusiness(data);
        if (data.tenor) {
          setNewTenor(String(data.tenor));
          setNewMarginRate(String(data.profitMarginRate || TENOR_MARGIN_DEFAULTS[data.tenor] || 10));
        }
      })
      .catch((e) => {
        console.error(e);
        setError("Failed to load transaction details.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (id) getBusiness(id);
  }, [id]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCurrentBusiness((prev) => ({ ...prev, [name]: value }));
  };

  const handleTenorPreset = (months) => {
    setNewTenor(String(months));
    const defaultRate = TENOR_MARGIN_DEFAULTS[months] !== undefined ? String(TENOR_MARGIN_DEFAULTS[months]) : newMarginRate;
    setNewMarginRate(defaultRate);
  };

  // Restructuring calculations
  const isBuy = currentBusiness?.creditOrDebit === "credit";
  const costAmt = Number(currentBusiness?.costAmount) || (Number(currentBusiness?.amount) - (Number(currentBusiness?.profitMarginAmount) || 0)) || Number(currentBusiness?.amount) || 0;
  const currentTenorNum = Number(currentBusiness?.tenor) || 0;
  const currentMarginRateNum = Number(currentBusiness?.profitMarginRate) || 0;
  const currentTotalAmount = Number(currentBusiness?.amount) || 0;

  const nTenorNum = parseInt(newTenor, 10) || currentTenorNum;
  const nMarginRateNum = parseFloat(newMarginRate) || 0;
  const previewNewMarginAmt = Number(((costAmt * nMarginRateNum) / 100).toFixed(2));
  const previewNewTotalAmt = Number((costAmt + previewNewMarginAmt).toFixed(2));
  const deltaAmt = Number((previewNewTotalAmt - currentTotalAmount).toFixed(2));
  const newMonthly = nTenorNum > 0 ? Number((previewNewTotalAmt / nTenorNum).toFixed(2)) : 0;

  const handleApplyNewTenor = async () => {
    setError("");
    setMessage("");
    if (!nTenorNum || nTenorNum <= 0) {
      setError("Please specify a valid tenor in months.");
      return;
    }

    setUpdating(true);
    try {
      const payload = {
        newTenor: nTenorNum,
        newMarginRate: nMarginRateNum,
        reason: tenorReason || `Member could not meet up with original ${currentTenorNum}m tenor. Restructured to ${nTenorNum}m.`
      };

      const res = await BusinessDataService.changeTenor(
        currentBusiness._id || currentBusiness.id,
        payload,
        currentBusiness.owner
      );

      const updatedDoc = res.data?.transaction || currentBusiness;
      setCurrentBusiness(updatedDoc);
      setMessage(res.data?.message || "Tenor and profit margin updated successfully!");
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || "Failed to update tenor");
    } finally {
      setUpdating(false);
    }
  };

  const updateBusiness = () => {
    setError("");
    setMessage("");
    setUpdating(true);
    BusinessDataService.update(currentBusiness.id || currentBusiness._id, currentBusiness)
      .then(() => {
        setMessage("Business transaction updated successfully!");
      })
      .catch((e) => {
        console.error(e);
        setError("Failed to update transaction.");
      })
      .finally(() => setUpdating(false));
  };

  const deleteBusiness = () => {
    if (!window.confirm("Are you sure you want to delete this business record?")) return;
    BusinessDataService.remove(currentBusiness.id || currentBusiness._id, currentBusiness.owner)
      .then(() => {
        navigate(-1);
      })
      .catch((e) => {
        console.error(e);
        setError("Failed to delete transaction.");
      });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentBusiness) {
    return (
      <Paper sx={{ maxWidth: 640, mx: "auto", my: 4, p: 3, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">No business transaction found.</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ maxWidth: 840, mx: "auto", my: 4, p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate(-1)} sx={{ textTransform: "none", fontWeight: 700 }}>
          Back
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "#0B3B60" }}>
          Business Transaction Details
        </Typography>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2.5 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

      {/* Transaction Overview Card */}
      <Card
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2.5,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Type</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={isBuy ? "Buy (Goods Purchase)" : "Repayment (Debit)"}
                size="small"
                sx={{
                  fontWeight: 800,
                  bgcolor: isBuy ? alpha("#0D9488", 0.12) : alpha("#0B3B60", 0.12),
                  color: isBuy ? "#0D9488" : "#0B3B60"
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Date</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{currentBusiness.date || "N/A"}</Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Total Billed</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: "#0B3B60" }}>
              ₦{fNumber(currentBusiness.amount || 0)}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>New Balance</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              ₦{fNumber(currentBusiness.newBalance || 0)}
            </Typography>
          </Grid>

          {isBuy && (
            <>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Cost of Goods</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  ₦{fNumber(costAmt)}
                </Typography>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Profit Margin</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#059669" }}>
                  +₦{fNumber(currentBusiness.profitMarginAmount || 0)} ({currentBusiness.profitMarginRate || 0}%)
                </Typography>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Current Tenor</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: "#0D9488" }}>
                  {currentBusiness.tenor ? `${currentBusiness.tenor} Months` : "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Original Tenor</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {currentBusiness.originalTenor ? `${currentBusiness.originalTenor} Months` : `${currentBusiness.tenor || "N/A"} Months`}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Card>

      {/* TENOR RESTRUCTURING PANEL FOR BUY TRANSACTIONS */}
      {isBuy && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            bgcolor: alpha("#0D9488", 0.05),
            border: `1.5px solid ${alpha("#0D9488", 0.3)}`
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <TrendingUpRoundedIcon sx={{ color: "#0D9488", fontSize: 24 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0D9488" }}>
                Restructure Tenor & Re-apply Profit Margin
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                If the user did not meet up with the initial tenor, select the extended tenor below to re-apply the profit margin.
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155", display: "block", mb: 0.75 }}>
                Select New Tenor:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {[
                  { m: 3, label: "3 Months (5%)" },
                  { m: 6, label: "6 Months (10%)" },
                  { m: 9, label: "9 Months (12%)" },
                ].map(({ m, label }) => {
                  const isSelected = String(newTenor) === String(m);
                  return (
                    <Button
                      key={m}
                      size="small"
                      variant={isSelected ? "contained" : "outlined"}
                      onClick={() => handleTenorPreset(m)}
                      sx={{
                        py: 0.75,
                        px: 2,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 800,
                        bgcolor: isSelected ? "#0D9488" : "background.paper",
                        color: isSelected ? "#FFFFFF" : "#0D9488",
                        borderColor: "#0D9488",
                        "&:hover": { bgcolor: isSelected ? "#0F766E" : alpha("#0D9488", 0.08) }
                      }}
                    >
                      {label}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="New Tenor (Months)"
                  size="small"
                  type="number"
                  fullWidth
                  value={newTenor}
                  onChange={(e) => setNewTenor(e.target.value)}
                  inputProps={{ min: 1, max: 60 }}
                  helperText="Enter 3, 6, 9 or custom tenor"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="New Profit Margin %"
                  size="small"
                  type="number"
                  fullWidth
                  value={newMarginRate}
                  onChange={(e) => setNewMarginRate(e.target.value)}
                  inputProps={{ min: 0, step: 0.5 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                  helperText="Officer can customize margin %"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Restructuring Reason / Note"
                  size="small"
                  fullWidth
                  value={tenorReason}
                  onChange={(e) => setTenorReason(e.target.value)}
                  placeholder="e.g. Member requested tenor extension due to delayed inventory sales"
                />
              </Grid>
            </Grid>

            {/* Restructure Preview Box */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "background.paper",
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", display: "block", mb: 1 }}>
                RESTRUCTURING PREVIEW
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>New Profit Margin:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: "#059669" }}>
                    ₦{fNumber(previewNewMarginAmt)} ({nMarginRateNum}%)
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>Balance Adjustment:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: deltaAmt >= 0 ? "#D97706" : "#059669" }}>
                    {deltaAmt >= 0 ? `+₦${fNumber(deltaAmt)}` : `-₦${fNumber(Math.abs(deltaAmt))}`}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>New Total Billed:</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "#0B3B60" }}>
                    ₦{fNumber(previewNewTotalAmt)}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>New Monthly ({nTenorNum}m):</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: "#0D9488" }}>
                    ₦{fNumber(newMonthly)} / mo
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Button
              variant="contained"
              onClick={handleApplyNewTenor}
              disabled={updating || (nTenorNum === currentTenorNum && nMarginRateNum === currentMarginRateNum)}
              startIcon={updating ? <CircularProgress size={18} /> : <CheckCircleRoundedIcon />}
              sx={{
                bgcolor: "#0D9488",
                fontWeight: 800,
                py: 1.25,
                borderRadius: 2,
                "&:hover": { bgcolor: "#0F766E" }
              }}
            >
              Apply New Tenor & Re-calculate Margin
            </Button>
          </Stack>

          {/* Tenor Extension History */}
          {Array.isArray(currentBusiness.tenorHistory) && currentBusiness.tenorHistory.length > 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: `1px dashed ${alpha("#0D9488", 0.3)}` }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <HistoryRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary" }}>
                  Tenor Restructuring History ({currentBusiness.tenorHistory.length})
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {currentBusiness.tenorHistory.map((h, idx) => (
                  <Box key={idx} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "background.paper", border: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                    <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                      Changed from {h.previousTenor}m ({h.previousMarginRate}%) to {h.newTenor}m ({h.newMarginRate}%)
                      • {h.deltaAmount >= 0 ? `+₦${fNumber(h.deltaAmount)}` : `-₦${fNumber(Math.abs(h.deltaAmount))}`} adjustment
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                      {new Date(h.changedAt).toLocaleString()} • {h.reason}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
      )}

      {/* Edit Description & Standard Details */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Edit Description
        </Typography>
        <TextField
          id="description"
          name="description"
          label="Description"
          fullWidth
          multiline
          rows={2}
          value={currentBusiness.description || ""}
          onChange={handleInputChange}
          sx={{ mb: 3 }}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between">
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={deleteBusiness}
            sx={{ borderRadius: 2 }}
          >
            Delete Record
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={updateBusiness}
            disabled={updating}
            sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}
          >
            Save Description
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

export default Business;
